import * as Sentry from "@sentry/react";

/**
 * Detect a stale/removed lazy-loaded chunk (react-player loads its provider chunks on demand, with
 * build-hashed filenames). After a deploy, an open tab asks for a chunk that no longer exists and the
 * dynamic import() rejects. The wording differs per browser: Chrome "Failed to fetch dynamically
 * imported module: <url>", Firefox "error loading dynamically imported module: <url>", Safari
 * "Importing a module script failed.". When the host serves index.html for the missing file, WebKit
 * reports "'text/html' is not a valid JavaScript MIME type." and Chrome "Expected a JavaScript module
 * script but the server responded with a MIME type of "text/html"".
 *
 * The error reaches Sentry via Sentry.ErrorBoundary, so the exception is chained: the first value is the
 * wrapper ("React ErrorBoundary TypeError") and the original TypeError comes after it. Every value is
 * checked, scoped to TypeError types, so unrelated errors that merely mention these words keep being reported.
 * @param {import('@sentry/react').Event} event
 * @returns {boolean}
 */
const isStaleChunkLoadError = (event) => {
    const values = event.exception?.values || [];
    return values.some(exception =>
        (exception.type || '').endsWith('TypeError')
        && /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'text\/html' is not a valid JavaScript MIME type|Expected a JavaScript.*module script but the server responded with a MIME type of "text\/html"/.test(exception.value || '')
    );
};

/**
 * Detect a "Maximum call stack size exceeded" whose frames don't resolve to our own bundle
 * (/assets/*.js) - seen exclusively on Chrome Mobile iOS, alternating between two minified
 * functions with frame filenames pointing at the page URL/origin rather than a real script file.
 * Likely a recursive bug in an injected script (e.g. a translation/accessibility extension)
 * reacting to our DOM changes, not our own code. A genuine stack overflow in app code would show
 * real bundle filenames, so this is left unmatched.
 * @param {import('@sentry/react').Event} event
 * @returns {boolean}
 */
const isNonAppStackOverflow = (event) => {
    const exception = event.exception?.values?.[0];
    if (exception?.type !== 'RangeError' || !exception.value?.includes('Maximum call stack size exceeded')) {
        return false;
    }
    const frames = exception.stacktrace?.frames || [];
    return frames.length > 0 && frames.every(frame => !/\/assets\/.+\.js$/.test(frame.filename || ''));
};

/**
 * Initialize Sentry. Does nothing when VITE_SENTRY_DNS is not set.
 */
const initSentry = () => {
    const dsn = import.meta.env.VITE_SENTRY_DNS;
    if (!dsn) {
        return;
    }
    Sentry.init({
        dsn,
        integrations: [],
        environment: import.meta.env.VITE_ENVIRONMENT_TAG ?? 'development',
        tracesSampleRate: 1.0,
        allowUrls: [import.meta.env.VITE_URL],
        release: import.meta.env.VITE_RELEASE_NUMBER ?? '0',
        beforeSend: (event) => {
            if (isStaleChunkLoadError(event)) {
                if (import.meta.env.DEV) {
                    console.error('Stale chunk load error, dropping from Sentry:', event);
                }
                return null;
            }
            if (isNonAppStackOverflow(event)) {
                if (import.meta.env.DEV) {
                    console.error('Non-app stack overflow, dropping from Sentry:', event);
                }
                return null;
            }
            return event;
        },
        ignoreErrors: [
            'Invalid call to runtime.sendMessage',
            /Object Not Found Matching Id.+/,
            /Loading chunk 446 failed.+/,
            'Event `Event` (type=error) captured as promise rejection',
            'An event processor returned `null`, will not send event.', //Sentry self-inflected
            /Converting circular structure to JSON[\s\S]*constructor 'HTML\w+Element'/, //DOM node serialization, likely browser extension
            'No identifiers allowed directly after numeric literal', //Firefox iOS content-blocker script mangling
            'Invalid call to browser.storage.local.set(). Exceeded storage quota.', //Safari mobile extension noise, not called by app code
            "Can't find variable: SCDynimacBridge", //Snapchat in-app browser bridge, not called by app code
            'Load failed', //Safari/WebKit fetch() network failure, e.g. backgrounded tab or connectivity blip
            /Failed to execute 'querySelectorAll' on 'Document':.*gpt-.*is not a valid selector\./, //Ad-blocker extension's hideAdNodes routine, not called by app code
            /__firefox__/, //Firefox iOS's own injected page bridge (reader mode, content blocker helpers), not called by app code
            /window\.ethereum/, //MetaMask/crypto-wallet extension injection, not called by app code
            'WKWebView API client did not respond to this postMessage', //iOS WKWebView internal bridge, not called by app code
            'Script message handler is unavailable', //In-app browser/WKWebView script bridge, not called by app code
            /kp\.apiget\.ru/, //XHR to a third-party domain injected by a browser extension, not called by app code
            /null is not an object \(evaluating 'localStorage\.getItem'\)|Can't find variable: localStorage/ //WebViews with storage disabled; throws in useI18n.setupLanguage before the app can render
        ]
    });
};

export { initSentry, isStaleChunkLoadError, isNonAppStackOverflow };
